/**
 * PreviewBodies — chart + drawing renderers used by the Content Editor
 * Preview modal AND the Student MyDesk LessonView.
 *
 * Why this rewrite (Feb 2026)
 * ────────────────────────────
 * The first version pre-validated images with `new Image()` and bailed out
 * to a structure-list when the pre-load fired `onerror` — which it
 * sometimes did spuriously for very large base64 PNGs (200KB+ DeskeyChart
 * exports). That left the preview with an empty card.
 *
 * New rendering strategy is simpler AND tougher:
 *   1. Render the saved PNG immediately (no pre-validation).
 *   2. If it errors at decode time, fall through to a deterministic SVG
 *      reconstructed from `chart.nodes` + `chart.connectors` (see
 *      `FlowchartSVG.jsx`). Vector, crisp, always loads.
 *   3. If THAT can't render either (no nodes), show a friendly placeholder.
 *
 * Drawings (raster only — no structured data to fall back to) get a
 * similar simple two-state renderer: image OR placeholder.
 */
import React, { useState } from 'react';
import { ImageOff, Copy, Check } from 'lucide-react';
import FlowchartSVG from '../../FlowchartSVG';
import SafeImage from '../../SafeImage';

/**
 * Local Img shim — delegates to the shared SafeImage framework so charts
 * and drawings inherit lazy-mount (IntersectionObserver), skeleton, and
 * the click-to-open zoomable lightbox uniformly.
 */
function Img({ src, alt, className = '', style = {}, onError }) {
  return (
    <SafeImage
      src={src}
      alt={alt}
      className={className}
      style={style}
      zoomable
      skeleton={false}   /* parent figure already carries a bg; skip double skeleton */
      onReady={(ok) => { if (!ok) onError?.(); }}
    />
  );
}

/**
 * ChartPreviewBody — renders a flowchart in three tiers of resilience.
 *
 * Tier 1 (preferred): saved PNG export rendered as-is.
 * Tier 2 (auto-fallback): inline SVG reconstructed from nodes + connectors
 *                          (crisp vector, no decode cost).
 * Tier 3 (last resort): structure-list summary so the user still sees
 *                          a meaningful preview of what's inside.
 *
 * @param ch — chart object: { image_url, nodes, connectors, theme, title }
 * @param accent — theme accent color
 * @param sBody / sMuted — body / muted text classes from the active theme
 * @param isDark — whether the preview theme is dark (controls bg under the diagram)
 */
export function ChartPreviewBody({ ch, accent = '#0d9488', sBody = '', sMuted = '', isDark = false }) {
  const [imgErr, setImgErr] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasImage = !!(ch?.image_url && typeof ch.image_url === 'string' && ch.image_url.length > 32);
  const hasNodes = Array.isArray(ch?.nodes) && ch.nodes.length > 0;
  const bg = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';

  // ── Tier 1: PNG export
  if (hasImage && !imgErr) {
    return (
      <figure className="relative" style={{ background: bg }} data-testid="chart-preview-image">
        <Img
          src={ch.image_url}
          alt={ch.title || 'Flowchart'}
          className="w-full block object-contain mx-auto"
          style={{ background: '#ffffff', maxHeight: 560, padding: 8 }}
          onError={() => setImgErr(true)}
        />
      </figure>
    );
  }

  // ── Tier 2: structured SVG re-render
  if (hasNodes) {
    return (
      <figure className="relative p-4" style={{ background: bg }} data-testid="chart-preview-svg">
        <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: `${accent}22` }}>
          <FlowchartSVG nodes={ch.nodes} connectors={ch.connectors || []} accent={accent} />
        </div>
        <div className={`flex items-center justify-between mt-2 text-[10px] ${sMuted}`}>
          <span className="uppercase tracking-widest font-bold">SVG render · {(ch.nodes || []).length} nodes · {(ch.connectors || []).length} edges</span>
          {hasImage && (
            <button
              onClick={() => { navigator.clipboard?.writeText(ch.image_url || ''); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="inline-flex items-center gap-1 hover:underline">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy PNG'}
            </button>
          )}
        </div>
      </figure>
    );
  }

  // ── Tier 3: placeholder
  return (
    <div className={`px-5 py-8 text-center ${sMuted}`} data-testid="chart-preview-empty">
      <ImageOff className="w-5 h-5 mx-auto mb-2 opacity-50" />
      <p className="text-[12px]">No preview saved — open this flowchart in Studio to re-export.</p>
    </div>
  );
}

/**
 * DrawingPreviewBody — single raster drawing with a robust fallback.
 * Drawings are free-form pen strokes saved as base64 PNG — there's no
 * structured representation to reconstruct from, so the fallback is just
 * a friendly placeholder.
 *
 * @param url     — saved drawing image URL (usually base64 data:image/png)
 * @param caption — optional caption shown only by the parent card chrome
 * @param sMuted  — muted text class from the active theme
 */
export function DrawingPreviewBody({ url, caption = 'Drawing', sMuted = '' }) {
  const [err, setErr] = useState(false);
  const hasUrl = !!(url && typeof url === 'string' && url.length > 10);

  if (hasUrl && !err) {
    return (
      <div className="p-3" data-testid="drawing-preview-image">
        <Img
          src={url}
          alt={caption}
          className="w-full max-h-[520px] object-contain mx-auto rounded-lg block"
          onError={() => setErr(true)}
        />
      </div>
    );
  }

  return (
    <div className={`px-5 py-8 text-center ${sMuted}`} data-testid="drawing-preview-empty">
      <ImageOff className="w-5 h-5 mx-auto mb-2 opacity-50" />
      <p className="text-[12px]">No preview saved — re-open the drawing in Studio to re-export.</p>
    </div>
  );
}
